import { Fragment, useCallback, useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Fade,
  IconButton,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useStreamingChat } from 'Hooks/useStreamingChat';
import { ChatMessage, PartialAnswerNote, ToolCallProgress } from 'Models/chat';
import {
  useDeleteConversationMutation,
  useGetConversationsQuery,
  useLazyGetConversationQuery,
} from 'Services/conversations';
import {
  useGetHubspotOwnerLinkQuery,
  useUpdateProfileMutation,
} from 'Services/user';
import { OWNER_LINK_DECLINED } from 'Models/auth';
import ConversationSidebar, { SIDEBAR_WIDTH } from './ConversationSidebar';
import { advisor } from 'Components/AdvisorTheme';

const COLUMN_WIDTH = 820;

// ─── Assistant avatar ──────────────────────────────────────────────────────────

function AssistantAvatar({ accent }: { accent: string }) {
  return (
    <Avatar
      sx={{
        width: 34,
        height: 34,
        background: `linear-gradient(135deg, ${accent} 0%, #6870fa 100%)`,
        boxShadow: '0 2px 8px rgba(19,3,58,0.25)',
      }}
    >
      <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: 'white' }} />
    </Avatar>
  );
}

// ─── Tool call list ───────────────────────────────────────────────────────────

function ToolCallList({ toolCalls }: { toolCalls: ToolCallProgress[] }) {
  if (toolCalls.length === 0) return null;
  return (
    <Box
      sx={{
        mb: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(19,3,58,0.04)',
      }}
    >
      {toolCalls.map((tc, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tc.status === 'running' ? (
            <CircularProgress size={13} thickness={5} />
          ) : (
            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: 'success.main' }} />
          )}
          <Typography variant="caption" color="text.secondary">
            {tc.label}
            {tc.status === 'done' && tc.durationMs != null && (
              <> · {tc.durationMs}ms</>
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Partial answer marker ────────────────────────────────────────────────────

/**
 * Says that this answer was assembled from an investigation the server cut
 * short, not from a finished one (REV-200 Phase 7).
 *
 * Deliberately a footnote rather than a warning banner: the answer above it is
 * a real answer and is usually right, so the marker is here to explain a thin
 * one, not to cast doubt on every one. Same restraint as the compaction
 * divider — caption weight, secondary colour, detail behind a tooltip.
 */
const BUDGET_EXPLANATION: Record<PartialAnswerNote['budget'], string> = {
  iterations: 'reached its limit on investigation steps',
  tool_calls: 'reached its limit on data lookups',
  time: 'ran out of time',
};

function PartialAnswerMarker({ note }: { note: PartialAnswerNote }) {
  const steps = note.iterations === 1 ? '1 step' : `${note.iterations} steps`;
  const calls = note.toolCalls === 1 ? '1 lookup' : `${note.toolCalls} lookups`;

  return (
    <Tooltip
      title={`The assistant ${BUDGET_EXPLANATION[note.budget]} after ${steps} and ${calls}, and answered from the data it had gathered by then. Asking a narrower follow-up usually gets a fuller answer.`}
      placement="top"
      arrow
    >
      <Box
        sx={{
          mt: 1.25,
          pt: 1,
          borderTop: '1px solid rgba(16,24,40,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.secondary',
          cursor: 'default',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Answered from partial data
        </Typography>
      </Box>
    </Tooltip>
  );
}

// ─── Who asked (REV-203) ────────────────────────────────────────────────────────

/**
 * Whether this transcript has more than one asker.
 *
 * Conversations are shared across a company, so a sidebar entry can hold turns
 * from several people. A solo conversation is the common case, and a name on
 * every one of your own questions is noise — so labels appear only once a
 * second person has actually asked something.
 *
 * Counted over `userId`, not `displayName`: two colleagues can share a name,
 * and the turn you just sent carries no attribution at all until the transcript
 * is refetched, which must not read as a second person.
 */
function hasMultipleAskers(messages: ChatMessage[]): boolean {
  const askers = new Set(
    messages
      .filter((message) => message.role === 'user' && message.askedBy)
      .map((message) => message.askedBy!.userId),
  );
  return askers.size > 1;
}

/**
 * The caption for a user turn in a shared transcript.
 *
 * A turn with no attribution is the one you just sent — it has no `askedBy`
 * until the transcript is refetched, and the frontend has no source for your
 * own name. "You" keeps it from being the only unlabelled bubble in a labelled
 * transcript.
 */
function askerLabelFor(message: ChatMessage): string {
  return message.askedBy?.displayName ?? 'You';
}

// ─── HubSpot owner link prompt ─────────────────────────────────────────────────

/**
 * Offers the auto-matched HubSpot owner for confirmation (REV-204).
 *
 * Shown only for `suggested`. Answering either way moves the user out of that
 * state permanently and across devices — confirming stores the owner id,
 * declining stores the reserved sentinel — which is what makes this prompt
 * one-time without a local dismissal flag to keep.
 *
 * Deliberately not shown for `unlinked`. Having no HubSpot owner is normal for a
 * RevOps lead, a founder or an analyst, and nothing here should imply otherwise.
 */
function HubspotOwnerPrompt({ accent }: { accent: string }) {
  const { data: link } = useGetHubspotOwnerLinkQuery();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  if (link?.status !== 'suggested' || !link.ownerId) return null;

  const answer = (hubspotOwnerId: string) => {
    // Errors are deliberately swallowed: the prompt is an offer, and failing to
    // answer it must not interrupt the conversation the user came here for. The
    // suggestion simply reappears on the next load.
    updateProfile({ hubspotOwnerId })
      .unwrap()
      .catch(() => undefined);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 2,
        py: 1.5,
        mb: 2.5,
        borderRadius: 2.5,
        bgcolor: 'rgba(19,3,58,0.03)',
        border: '1px solid rgba(16,24,40,0.08)',
      }}
    >
      <BadgeRoundedIcon sx={{ fontSize: 20, color: accent }} />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 220 }}>
        Are you <strong>{link.ownerName}</strong> in HubSpot
        {link.ownerEmail ? ` (${link.ownerEmail})` : ''}? Linking lets the assistant
        answer questions about your own deals.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={isLoading}
          onClick={() => answer(link.ownerId as string)}
          sx={{ bgcolor: accent, borderRadius: 2, textTransform: 'none' }}
        >
          Yes, that's me
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={isLoading}
          onClick={() => answer(OWNER_LINK_DECLINED)}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Not me
        </Button>
      </Box>
    </Box>
  );
}

// ─── Single message row ─────────────────────────────────────────────────────────

function MessageRow({
  message,
  accent,
  askerLabel,
}: {
  message: ChatMessage;
  accent: string;
  /** Set only for a user turn in a shared transcript; omitted otherwise. */
  askerLabel?: string;
}) {
  const isUser = message.role === 'user';

  const bubbleContent = () => {
    if (message.role === 'assistant' && message.isStreaming && message.content === '') {
      return (
        <Box sx={{ width: 220 }}>
          <Skeleton animation="wave" />
          <Skeleton animation="wave" width="80%" />
        </Box>
      );
    }
    if (isUser) {
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message.content}
        </Typography>
      );
    }
    return (
      <Typography
        variant="body2"
        component="div"
        sx={{
          wordBreak: 'break-word',
          lineHeight: 1.6,
          '& p': { margin: 0 },
          '& p + p': { mt: 1 },
          '& ul, & ol': { mt: 0.5, mb: 0.5, pl: 2.5 },
          '& code': {
            bgcolor: 'rgba(19,3,58,0.06)',
            px: 0.5,
            py: 0.1,
            borderRadius: 0.5,
            fontSize: '0.85em',
          },
          // Tables arrive from the assistant far more often since REV-202 —
          // pipeline value by stage, source breakdowns, period comparisons and
          // rep rankings are all tabular. See the `remarkGfm` note below.
          '& th, & td': {
            border: '1px solid rgba(16,24,40,0.12)',
            px: 1,
            py: 0.5,
            textAlign: 'left',
            verticalAlign: 'top',
            whiteSpace: 'nowrap',
          },
          '& th': {
            bgcolor: 'rgba(19,3,58,0.04)',
            fontWeight: 600,
          },
        }}
      >
        {/*
          `remarkGfm` is not optional here. react-markdown ships CommonMark
          only, and pipe tables are a GitHub-Flavored Markdown extension — so
          without this plugin every table the assistant returns renders as a
          literal `| Stage | Value |` string. That was the state of the chat
          until REV-202 dev testing surfaced it: the bug predates the ticket,
          but the ticket is what made the assistant answer in tables often
          enough for anyone to notice.

          The `table` override is what keeps a wide one usable. Some answers
          run to seven columns, and the column is only ~620px, so the table
          scrolls sideways inside its own box rather than forcing the whole
          message to wrap or overflow.
        */}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ children }) => (
              <Box sx={{ overflowX: 'auto', my: 1 }}>
                <Box
                  component="table"
                  sx={{ borderCollapse: 'collapse', fontSize: '0.92em' }}
                >
                  {children}
                </Box>
              </Box>
            ),
          }}
        >
          {message.content}
        </ReactMarkdown>
        {message.isStreaming && <span style={{ opacity: 0.5 }}>▍</span>}
      </Typography>
    );
  };

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          gap: 1.25,
          mb: 2.5,
        }}
      >
        {isUser ? (
          <Avatar sx={{ width: 34, height: 34, bgcolor: 'grey.300', color: 'grey.700' }}>
            <PersonRoundedIcon sx={{ fontSize: 19 }} />
          </Avatar>
        ) : (
          <AssistantAvatar accent={accent} />
        )}

        <Box
          sx={{
            maxWidth: '78%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          {askerLabel && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, px: 0.5 }}
            >
              {askerLabel}
            </Typography>
          )}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 2.5,
              borderTopLeftRadius: isUser ? 20 : 6,
              borderTopRightRadius: isUser ? 6 : 20,
              bgcolor: isUser ? accent : 'white',
              color: isUser ? 'white' : 'text.primary',
              boxShadow: isUser
                ? '0 4px 14px rgba(19,3,58,0.18)'
                : '0 2px 10px rgba(16,24,40,0.06)',
              border: isUser ? 'none' : '1px solid rgba(16,24,40,0.06)',
            }}
          >
            {!isUser && <ToolCallList toolCalls={message.toolCalls} />}
            {bubbleContent()}
            {!isUser && message.partial && <PartialAnswerMarker note={message.partial} />}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}

// ─── Context compaction divider ───────────────────────────────────────────────

/**
 * Marks where the backend folded earlier turns into its rolling summary. The
 * turns above it are still on screen — only the model's view of them changed.
 */
function CompactionDivider() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5 }}>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(16,24,40,0.10)' }} />
      <Tooltip
        title="Earlier turns were condensed into a summary to stay within the model's context window. The assistant still has the gist of them."
        placement="top"
        arrow
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            cursor: 'default',
          }}
        >
          <UnfoldLessRoundedIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Earlier messages summarized
          </Typography>
        </Box>
      </Tooltip>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(16,24,40,0.10)' }} />
    </Box>
  );
}

// ─── Empty state with suggestions ───────────────────────────────────────────────

/**
 * The six preloaded questions, rewritten in REV-202 Phase 13.1 against what the
 * widened tool surface can actually reach. REV-200 Phase 10.6 established the
 * method and it is followed here: every prompt is walked back to the metric
 * behind it, and every prompt that ships was driven through the measurement
 * harness first (13.2) rather than reasoned about.
 *
 * **Evidence.** `rvo-backend/scripts/rev202-reproduce.ts suggestions 3`, three
 * runs of each, against the dev account and Bedrock on 2026-09-15. All six
 * answer 3/3. The raw turns are in `rvo-backend/docs/rev202-measurements/`.
 * `P1`–`P6` in that harness are these six prompts; `C1`–`C6` are the *previous*
 * six, kept at their old wording because Phases 4 and 12 measured the ticket's
 * before/after against them and that comparison has to keep reading.
 *
 * Kept unchanged — three runs each, still answered 3/3, and nothing this ticket
 * added widens them:
 *   2 Lead quality & drop-off  the contacts strategy's only representative
 *   3 Ideal customer flow      the closed-won strategy's only representative,
 *                              and the only tile reaching the two touchpoint
 *                              metrics
 *
 * Extended:
 *   1 Lowest-hanging fruit   gains "and how much deal value is sitting in each
 *                            stage right now?". DEAL_VALUE_BY_STAGE was written
 *                            on every sync and read by nothing until Phase 8;
 *                            Phase 8.10 folded it into the same `deals` call,
 *                            so the clause costs no extra lookup. All three
 *                            runs returned the same per-stage table and the
 *                            same $637,232 total, and all three labelled it a
 *                            snapshot rather than a period total.
 *
 * Merged:
 *   4 Improving or declining  was two tiles — "Improving over time" and
 *                             "Declining over time" — with the same sources,
 *                             the same underlying metrics and mirrored wording.
 *                             `compareTo` returns both periods and the deltas
 *                             in one call, so asking both directions costs what
 *                             asking one did. Merging them frees the tile that
 *                             pays for 5. It carries "judge each stage on how
 *                             it has changed rather than on its current level"
 *                             because without that clause two of three runs
 *                             filed a merely *low* stage under "declining".
 *
 * New, and the reason this rewrite exists:
 *   5 Rep performance        owner names were in the table all along and only
 *                            owner IDs were reachable; rep questions were three
 *                            of the harness's twenty-four. Takes the slot the
 *                            dropped "Activity vs. conversion" leaves. The
 *                            wording went through three measured drafts —
 *                            "which stage before the close" made the model
 *                            invent a "Negotiation" stage this pipeline does
 *                            not have, so it ships asking for stages "exactly
 *                            as they appear in the pipeline" instead. Known
 *                            limitation, honest rather than hidden: the
 *                            per-owner metric counts terminal stages, so the
 *                            top rep's biggest loss stage reads as
 *                            "08 - Closed Lost". True, and not useful — the fix
 *                            is a reducer change in `rvo-backend`, not a
 *                            rewording here.
 *   6 Where deals come from  source attribution was unreachable before Phase 8,
 *                            and questions of this shape used to fall back to
 *                            the closed-won strategy and report a 100% win
 *                            rate. It names the dimension in prose — "original
 *                            source, the channel a contact first arrived
 *                            through" — rather than leaning on the
 *                            HS_ANALYTICS_SOURCE default, so a tenant whose
 *                            richest dimension is a different one still gets
 *                            the question it was asked. All three runs named
 *                            the dimension back and agreed on every figure.
 *
 * Dropped:
 *   — Activity vs. conversion. Its inputs are real and it answered, but 3 asks
 *     for the same two touchpoint metrics as part of a question with more in
 *     it, and 1 already carries the conversion framing. It was the weakest
 *     claim on a tile once 5 and 6 had something new to say.
 *
 * Not added, deliberately, and each for a measured reason:
 *   — anything company-shaped. Asked how accounts split by industry, three runs
 *     on the same 7,165 companies answered "~150", "2,264" and "3,141", each
 *     stated as the breakdown. The shelf promotes no attributes, so the tool
 *     ships whole records, the result cap truncates, and what survives differs
 *     per run. A tile that answers differently each time it is clicked is worse
 *     than no tile. Revisit when companies are projected server-side.
 *   — anything about email engagement. Every open, click and reply rate on the
 *     one dev shelf carrying them is 0, so the shape cannot be shown to answer
 *     until a tenant with real rates confirms it.
 */
const SUGGESTIONS = [
  {
    icon: <InsightsRoundedIcon sx={{ fontSize: 20 }} />,
    title: 'Lowest-hanging fruit',
    prompt:
      'What funnel stages are the lowest hanging fruit to improve upon that will increase the overall funnel performance? And how much deal value is sitting in each stage right now?',
  },
  {
    icon: <WarningAmberRoundedIcon sx={{ fontSize: 20 }} />,
    title: 'Lead quality & drop-off',
    prompt:
      'Are we seeing an increase or decrease in lead quality? Where are leads falling out of the funnel — how do lead, MQL, opportunity and customer counts compare, how many go unqualified, and how long do contacts sit in each lifecycle stage before moving on? How does all of that compare with six months ago?',
  },
  {
    icon: <GroupsRoundedIcon sx={{ fontSize: 20 }} />,
    title: 'Ideal customer flow',
    prompt:
      'Based on the closed won customers in this data cohort, what does an optimal funnel performance look like (i.e., average days in each stage, average days in each stage to closed won, average number of marketing activities per stage, average number of sales activities per stage)?',
  },
  {
    icon: <TrendingUpRoundedIcon sx={{ fontSize: 20 }} />,
    title: 'Improving or declining',
    prompt:
      'Which funnel stages have improved and which have got worse compared with six and 12 months ago — conversion rate to the next stage, days in stage, win rate? Judge each stage on how it has changed rather than on its current level, and say why.',
  },
  {
    icon: <BadgeRoundedIcon sx={{ fontSize: 20 }} />,
    title: 'Rep performance',
    prompt:
      'Which sales reps are closing the most deals and winning at the highest rate, and which pipeline stage is each of them losing the most deals at? Use their names rather than their HubSpot owner IDs, and name each stage exactly as it appears in the pipeline.',
  },
  {
    icon: <HubRoundedIcon sx={{ fontSize: 20 }} />,
    title: 'Where deals come from',
    prompt:
      'Which acquisition channels bring in the most customers and the most wins, and which produce the most losses? Break it down by original source — the channel a contact first arrived through — rather than by their most recent touch.',
  },
];

function EmptyState({
  accent,
  onPick,
}: {
  accent: string;
  onPick: (prompt: string) => void;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 2,
      }}
    >
      <Avatar
        sx={{
          width: 64,
          height: 64,
          mb: 2.5,
          background: `linear-gradient(135deg, ${accent} 0%, #6870fa 100%)`,
          boxShadow: '0 8px 24px rgba(19,3,58,0.25)',
        }}
      >
        <AutoAwesomeRoundedIcon sx={{ fontSize: 32, color: 'white' }} />
      </Avatar>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
        How can I help?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 460 }}>
        Ask anything about your HubSpot pipeline, deals, and revenue.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
          width: '100%',
          maxWidth: 620,
        }}
      >
        {SUGGESTIONS.map((s) => (
          <Tooltip key={s.title} title={s.prompt} placement="top" arrow>
          <Box
            component="button"
            type="button"
            onClick={() => onPick(s.prompt)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.75,
              textAlign: 'left',
              borderRadius: 2.5,
              bgcolor: 'white',
              border: '1px solid rgba(16,24,40,0.08)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: accent,
                boxShadow: '0 6px 18px rgba(19,3,58,0.10)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: 'rgba(19,3,58,0.06)',
                color: accent,
                flexShrink: 0,
              }}
            >
              {s.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {s.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {s.prompt}
              </Typography>
            </Box>
          </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}

// ─── Chat input bar ───────────────────────────────────────────────────────────

function ChatInput({
  onSend,
  disabled,
  accent,
  inputRef,
  draft,
  draftVersion,
}: {
  draftVersion?: number;
  draft?: string;
  onSend: (text: string) => void;
  disabled: boolean;
  accent: string;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const [input, setInput] = useState('');
  useEffect(() => { if (draft !== undefined) { setInput(draft); inputRef.current?.focus(); } }, [draft, draftVersion, inputRef]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !disabled && !!input.trim();

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, pt: 1 }}>
      <Box
        sx={{
          maxWidth: COLUMN_WIDTH,
          mx: 'auto',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          p: 0.75,
          pl: 2,
          borderRadius: 3.5,
          bgcolor: 'white',
          border: '1px solid rgba(16,24,40,0.10)',
          boxShadow: '0 4px 20px rgba(16,24,40,0.08)',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:focus-within': {
            borderColor: accent,
            boxShadow: `0 4px 24px rgba(19,3,58,0.14)`,
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={6}
          inputRef={inputRef}
          placeholder="Ask about your HubSpot data…"
          inputProps={{ "aria-label": "Ask RevOptica message" }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{ '& .MuiInputBase-root': { py: 1, fontSize: 16 } }}
        />
        <IconButton
          aria-label="Send message"
          onClick={handleSend}
          disabled={!canSend}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            bgcolor: canSend ? accent : 'rgba(16,24,40,0.10)',
            color: 'white',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: canSend ? accent : 'rgba(16,24,40,0.10)', opacity: 0.9 },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.8)' },
          }}
        >
          <SendRoundedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 1, opacity: 0.7 }}
      >
        Press Enter to send · Shift + Enter for a new line
      </Typography>
    </Box>
  );
}

// ─── Chat page ────────────────────────────────────────────────────────────────

export default function ChatPage({ draft, tenantKey, draftVersion }: { draft?: string; tenantKey?: string; draftVersion?: number }) {
  const theme = useTheme();
  // Rendered only inside the Ask RevOptica shell, so it follows the advisor purple.
  const accent = advisor.purple;
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const {
    data: conversations = [],
    isLoading: isListLoading,
    isError: isListError,
    refetch: refetchConversations,
  } = useGetConversationsQuery(tenantKey);
  const [fetchConversation] = useLazyGetConversationQuery();
  const [deleteConversation, { isError: isDeleteError }] = useDeleteConversationMutation();

  const {
    cancelActiveRequest,
    messages,
    sendMessage,
    isLoading,
    error,
    conversationId,
    compactedBeforeMessageId,
    loadConversation,
    startNewConversation,
  } = useStreamingChat({ onConversationCreated: () => refetchConversations() });

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Row with a request in flight — being fetched for resume, or being deleted.
  const [historyError, setHistoryError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const chatRootRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRequest = useRef(0);
  useEffect(() => () => { historyRequest.current += 1; }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // A finished turn moves the conversation's `updatedAt` and `message_count`,
  // which are what the listing sorts and labels on.
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !isLoading) refetchConversations();
    wasStreamingRef.current = isLoading;
  }, [isLoading, refetchConversations]);

  const openConversation = useCallback(
    async (id: string) => {
      setDrawerOpen(false);
      if (id === conversationId) return;
      const request = ++historyRequest.current;
      setBusyId(id);
      setHistoryError(false);
      try {
        const transcript = await fetchConversation(id).unwrap();
        if (request === historyRequest.current) loadConversation(transcript);
      } catch {
        if (request !== historyRequest.current) return;
        setHistoryError(true);
        // A conversation deleted from another device 404s — refresh the listing
        // so the stale row goes away rather than reporting a chat error.
        refetchConversations();
      } finally {
        if (request === historyRequest.current) setBusyId(null);
      }
    },
    [conversationId, fetchConversation, loadConversation, refetchConversations],
  );

  const handleNewConversation = useCallback(() => {
    historyRequest.current += 1;
    setBusyId(null);
    setDrawerOpen(false);
    startNewConversation();
    inputRef.current?.focus();
  }, [startNewConversation]);

  const handleDeleteConversation = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        await deleteConversation(id).unwrap();
        // The transcript on screen no longer exists anywhere; drop back to empty.
        if (id === conversationId) startNewConversation();
      } catch {
        // Surfaced in the sidebar off the mutation's own error state.
      } finally {
        setBusyId(null);
      }
    },
    [conversationId, deleteConversation, startNewConversation],
  );

  // Pick up where the user left off (13.7). One shot, as soon as the listing
  // settles: a turn sent before then owns the page, and every later switch is
  // an explicit click.
  const didAutoLoadRef = useRef(false);
  useEffect(() => {
    if (didAutoLoadRef.current || isListLoading) return;
    didAutoLoadRef.current = true;
    const mostRecent = conversations[0]; // the backend orders newest first
    if (!mostRecent || conversationId || messages.length > 0) return;
    openConversation(mostRecent.conversationId);
  }, [isListLoading, conversations, conversationId, messages.length, openConversation]);

  const handleSuggestion = (prompt: string) => {
    historyRequest.current += 1;
    setBusyId(null);
    sendMessage(prompt);
    inputRef.current?.focus();
  };

  const isEmpty = messages.length === 0;

  // Computed once per transcript rather than per row: the answer is a property
  // of the conversation, not of any one turn (REV-203).
  const isSharedTranscript = useMemo(() => hasMultipleAskers(messages), [messages]);

  const sidebar = (
    <ConversationSidebar
      conversations={conversations}
      isLoading={isListLoading}
      activeId={conversationId}
      busyId={busyId}
      accent={accent}
      deleteError={isDeleteError}
      onSelect={openConversation}
      onNew={handleNewConversation}
      onDelete={handleDeleteConversation}
    />
  );

  return (
    <Box
      ref={chatRootRef}
      sx={{
        display: 'flex',
        height: "100%",
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #f7f7fb 0%, #eef0f6 100%)',
      }}
    >
      {/* Conversation list — a column at md and up, a drawer below it */}
      {isDesktop ? (
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            minHeight: 0,
            borderRight: '1px solid rgba(16,24,40,0.06)',
          }}
        >
          {sidebar}
        </Box>
      ) : (
        <Drawer
          open={drawerOpen}
          container={() => chatRootRef.current}
          ModalProps={{ disablePortal: true }}
          sx={{ zIndex: theme.zIndex.modal + 1 }}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
        >
          {sidebar}
        </Drawer>
      )}

      {/* Message column */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderBottom: '1px solid rgba(16,24,40,0.06)',
            bgcolor: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {!isDesktop && (
            <IconButton
              aria-label="Open conversations"
              onClick={() => setDrawerOpen(true)}
              sx={{ ml: -0.75, color: 'text.secondary' }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}
          <AssistantAvatar accent={accent} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              RevOptica Assistant
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AI insights for your HubSpot pipeline
            </Typography>
          </Box>
        </Box>

        {/*
          Outside the scroll container on purpose. Inside it, the prompt sits
          above the transcript and scrolls away the moment a conversation has
          messages — and the view auto-scrolls to the newest turn, so it is
          off-screen exactly when someone is using the page. Pinned here it stays
          until answered, which is the whole point of asking.
        */}
        <Box sx={{ px: { xs: 2, sm: 3 }, width: '100%' }}>
          <Box sx={{ width: '100%', maxWidth: COLUMN_WIDTH, mx: 'auto', pt: 2 }}>
            <HubspotOwnerPrompt accent={accent} />
          </Box>
        </Box>

        {(isListError || historyError) && <Box role="alert" sx={{ p: 2 }}>Conversation history unavailable. <Button onClick={() => { setHistoryError(false); refetchConversations(); }}>Retry history</Button></Box>}
        {/* Scrollable message area */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2, sm: 3 },
            py: 3,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: COLUMN_WIDTH,
              mx: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isEmpty ? (
              <EmptyState accent={accent} onPick={handleSuggestion} />
            ) : (
              messages.map((msg) => (
                <Fragment key={msg.id}>
                  {msg.id === compactedBeforeMessageId && <CompactionDivider />}
                  <MessageRow
                    message={msg}
                    accent={accent}
                    askerLabel={
                      isSharedTranscript && msg.role === 'user'
                        ? askerLabelFor(msg)
                        : undefined
                    }
                  />
                </Fragment>
              ))
            )}
            {error && (
              <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 1 }}>
                {error}
                <Button disabled={isLoading} onClick={() => { const last = [...messages].reverse().find(message => message.role === "user"); if (last) sendMessage(last.content); }}>Retry last question</Button>
              </Typography>
            )}
            <div ref={messagesEndRef} />
          </Box>
        </Box>

        {isLoading && <Button onClick={cancelActiveRequest}>Stop generating</Button>}
        {/* Input bar */}
        <ChatInput
          draft={draft}
          draftVersion={draftVersion}
          onSend={sendMessage}
          disabled={isLoading || !!busyId}
          accent={accent}
          inputRef={inputRef}
        />
      </Box>
    </Box>
  );
}
